import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { OpenSlotRow, ShopRow } from "@/types/db";
import type {
  DateString,
  MobileStep,
  OpenSlotsByDateAndShopId,
  SelectedOpenSlotIdsByShopId,
  ShopId,
} from "./types";
import type {
  WeeklyOpenSlotItem,
} from "./responses";
import { RootState } from "@/store";
import { useAppSelector } from "@/store/hooks";
import { fetchWeeklyOpenSlots } from "@/api/openSlots";
import { buildSearch } from "@/utils/query";
import DesktopDrawer from "./components/DesktopDrawer";
import { dateToString, formatDateFull, formatTimeCode } from "@/utils/schedule";
import { getShopBadgeClass } from "./color";
import TimelinePreview from "./components/TimeLinePreview";
import DressTourTimeLine, { DressTourTimelineSlot } from "@/components/combinations/DressTourTimeLine";
import ShopNaverMap from "@/components/common/NaverMap/ShopNaverMap";
import ReservationDateViewer from "@/components/common/reservation/ReservationDateViewer";
import { buildDirectSlotSelectAnalyticsPayload, DIRECT_SLOT, GA_EVENT, trackEvent } from "@/utils/gaUtil";
const DATE_BORDER_COLOR = '#d3d4d6'
const TIME_AXIS = [
  1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800,
];

const WEEK_DAYS = ["월", "화", "수", "목", "금", "토", "일"] as const;

function dateToStringLocal(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function generateWeekDates(weekStart: Date): DateString[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return dateToStringLocal(d);
  });
}

function formatDateRange(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);

  const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  return `${fmt(weekStart)} ~ ${fmt(end)}`;
}

function formatDateShort(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}





// async function fetchWeeklyOpenSlots(
//   startDate: string,
//   endDate: string,
//   shopIds: number[]
// ): Promise<OpenSlotRow[]> {
//   if (shopIds.length === 0) return [];

//   const { data } = await axios.get<GetWeeklyOpenSlotsResponse>(
//     "/api/open-slots/weekly",
//     {
//       params: {
//         startDate,
//         endDate,
//         shopIds: shopIds.join(","),
//       },
//     }
//   );

//   return data.data || [];
// }

function StepIndicator({ currentStep }: { currentStep: MobileStep }) {
  const steps = [
    { num: 1 as MobileStep, label: "업체 선택" },
    { num: 2 as MobileStep, label: "슬롯 선택" },
    { num: 3 as MobileStep, label: "일정 확인" },
  ];

  return (
    <div className="flex items-center gap-0 px-5 py-3 bg-white border-b border-[#E5E7EB]">
      {steps.map((step, idx) => (
        <div key={step.num} className="flex items-center flex-1">
          <div className="flex items-center gap-2 flex-1">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                currentStep > step.num
                  ? "bg-[#2563EB] text-white"
                  : currentStep === step.num
                  ? "bg-[#2563EB] text-white ring-4 ring-[#DBEAFE]"
                  : "bg-[#F3F4F6] text-[#9CA3AF]"
              }`}
            >
              {currentStep > step.num ? (
                <i className="ri-check-line text-xs" />
              ) : (
                step.num
              )}
            </div>
            <span
              className={`text-xs font-medium whitespace-nowrap ${
                currentStep === step.num
                  ? "text-[#2563EB] font-semibold"
                  : currentStep > step.num
                  ? "text-[#2563EB]"
                  : "text-[#9CA3AF]"
              }`}
            >
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`flex-1 h-px mx-2 transition-colors ${
                currentStep > step.num ? "bg-[#2563EB]" : "bg-[#E5E7EB]"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

type ShopSelectPanelProps = {
  shops: ShopRow[];
  selectedShopIds: ShopId[];
  onToggleShop: (shopId: ShopId) => void;
  onNext?: () => void;
  mobile?: boolean;
};

function ShopSelectPanel({
  shops,
  selectedShopIds,
  onToggleShop,
  onNext,
  mobile = false,
}: ShopSelectPanelProps) {
  const [search, setSearch] = useState("");

  const filteredShops = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return shops;

    return shops.filter((shop) =>
      shop.shopName.toLowerCase().includes(keyword)
    );
  }, [search, shops]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 bg-white border-b border-[#E5E7EB] flex-shrink-0">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm" />
          <input
            type="text"
            placeholder="업체명 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#2563EB] transition-all"
          />
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2">
          방문할 업체를 선택하세요 · 복수 선택 가능
        </p>
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        {filteredShops.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-8">
            <div className="w-12 h-12 flex items-center justify-center bg-[#F3F4F6] rounded-xl mb-3">
              <i className="ri-search-line text-xl text-[#9CA3AF]" />
            </div>
            <p className="text-sm font-semibold text-[#374151]">검색 결과가 없습니다</p>
          </div>
        ) : (
          filteredShops.map((shop) => {
            const isSelected = selectedShopIds.includes(shop.id);
            const selectedIndex = selectedShopIds.indexOf(shop.id);
            const badgeClass =
              selectedIndex >= 0 ? getShopBadgeClass(selectedIndex) : null;

            return (
              <button
                key={shop.id}
                onClick={() => onToggleShop(shop.id)}
                className={`w-full text-left px-4 py-4 border-b border-[#F3F4F6] last:border-b-0 flex items-center gap-3 transition-colors cursor-pointer ${
                  isSelected ? "bg-[#F8FAFF]" : "hover:bg-[#F9FAFB]"
                }`}
              >
                <div
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected
                      ? "bg-[#2563EB] border-[#2563EB]"
                      : "border-[#D1D5DB] bg-white"
                  }`}
                >
                  {isSelected && <i className="ri-check-line text-white text-xs" />}
                </div>

                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-semibold truncate ${
                      isSelected ? "text-[#1E3A8A]" : "text-[#111827]"
                    }`}
                  >
                    {shop.shopName}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] truncate mt-0.5">
                    {shop.address || "-"}
                  </p>
                </div>

                {isSelected && badgeClass && (
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${badgeClass.dot} text-white text-xs font-bold`}
                  >
                    {selectedIndex + 1}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      {mobile && (
        <div className="flex-shrink-0 bg-white border-t border-[#E5E7EB] px-4 py-4 safe-area-bottom">
          {selectedShopIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {selectedShopIds.map((shopId, idx) => {
                const shop = shops.find((item) => item.id === shopId);
                if (!shop) return null;
                const badgeClass = getShopBadgeClass(idx);

                return (
                  <span
                    key={shopId}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${badgeClass.bg} ${badgeClass.text} ${badgeClass.border}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${badgeClass.dot}`} />
                    {shop.shopName}
                  </span>
                );
              })}
            </div>
          )}

          <button
            onClick={onNext}
            disabled={selectedShopIds.length === 0}
            className={`w-full py-4 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
              selectedShopIds.length > 0
                ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                : "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
            }`}
          >
            <i className="ri-calendar-line text-base" />
            업체 선택 완료 ({selectedShopIds.length})
          </button>
        </div>
      )}
    </div>
  );
}

type SlotGridProps = {
  shops: ShopRow[];
  selectedShopIds: ShopId[];
  weekDates: DateString[];
  openSlotsByDateAndShopId: OpenSlotsByDateAndShopId;
  selectedOpenSlotIdsByShopId: SelectedOpenSlotIdsByShopId;
  onSelectOpenSlot: (openSlot: WeeklyOpenSlotItem) => void;
};

function SlotGrid({
  shops,
  selectedShopIds,
  weekDates,
  openSlotsByDateAndShopId,
  selectedOpenSlotIdsByShopId,
  onSelectOpenSlot,
}: SlotGridProps) {
  const selectedShops = useMemo(
    () =>
      selectedShopIds
        .map((shopId) => shops.find((shop) => shop.id === shopId))
        .filter((shop): shop is ShopRow => Boolean(shop)),
    [shops, selectedShopIds]
  );

  const selectedDate = useMemo(() => {
    const selectedIds = Object.values(selectedOpenSlotIdsByShopId);
    if (selectedIds.length === 0) return null;

    for (const dateStr of weekDates) {
      for (const shopId of selectedShopIds) {
        const slots = openSlotsByDateAndShopId[dateStr]?.[shopId] || [];
        const matched = slots.find((slot) => selectedIds.includes(slot.id));
        if (matched) return matched.slotDate;
      }
    }

    return null;
  }, [
    openSlotsByDateAndShopId,
    selectedOpenSlotIdsByShopId,
    selectedShopIds,
    weekDates,
  ]);

  const today = dateToStringLocal(new Date());

  return (
    <div className="overflow-x-auto">
      <table
        className="border-collapse text-xs"
        style={{
          minWidth: `${80 + weekDates.length * selectedShops.length * 72}px`,
        }}
      >
        <thead>
          <tr>
            <th className="w-14 py-2 text-[#9CA3AF] font-medium text-left pl-2 sticky left-0 bg-white z-20 border-b border-[#E5E7EB]">
              시간
            </th>

            {weekDates.map((dateStr, dayIdx) => {
              if (dayIdx === 0 || dayIdx === 6) return null
              const isToday = dateStr === today;
              const isSelectedDay = selectedDate === dateStr;
              
              return (
                <th
                  key={dateStr}
                  colSpan={selectedShops.length}
                  className={`py-2 text-center border-b border-[#E5E7EB] border-l border-l-[#F3F4F6] ${
                    isSelectedDay ? "bg-[#EFF6FF]" : isToday ? "bg-[#F9FAFB]" : ""
                  }`}
                  style={{ borderLeft: `1px solid ${DATE_BORDER_COLOR}`}}
                  
                >
                  <div className="flex flex-col items-center gap-0.5 px-1">
                    <span
                      className={`text-[10px] font-medium ${
                        isToday ? "text-[#2563EB]" : "text-[#9CA3AF]"
                      }`}
                    >
                      {WEEK_DAYS[dayIdx]}
                    </span>
                    <span
                      className={`text-xs font-bold ${
                        isToday
                          ? "text-[#2563EB]"
                          : isSelectedDay
                          ? "text-[#1D4ED8]"
                          : "text-[#374151]"
                      }`}
                    >
                      {formatDateShort(dateStr)}
                    </span>
                    {(isToday || isSelectedDay) ? (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" />
                    ): <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                </th>
              );
            })}
          </tr>

          <tr>
            <th className="sticky left-0 bg-white z-20 border-b border-[#E5E7EB]" />
            {weekDates.map((dateStr, index) => 
              (index === 0 || index === 6) ? null : 
              selectedShops.map((shop, shopIndex) => {

                const firstCell = shopIndex === 0
                const badgeClass = getShopBadgeClass(shopIndex);
                return (
                  <th
                    key={`${dateStr}-${shop.id}`}
                    className={`py-1.5 px-1 border-b border-[#E5E7EB] border-l border-l-${firstCell ? DATE_BORDER_COLOR : '[#F3F4F6]'} min-w-[68px] ${
                      selectedDate === dateStr ? "bg-[#EFF6FF]" : ""
                    }
                    `}
                    style={firstCell ? { borderLeft: `1px solid ${DATE_BORDER_COLOR}`} : {}}
                  >
                    <div
                      className={`flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-full ${badgeClass.bg} ${badgeClass.border} border`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${badgeClass.dot}`} />
                      <span
                        className={`text-[9px] font-semibold truncate ${badgeClass.text}`}
                        style={{ width: 52 }}
                      >
                        {shop.shopName.length > 6
                          ? `${shop.shopName.slice(0, 6)}…`
                          : shop.shopName}
                      </span>
                    </div>
                  </th>
                );
              })
            )}
          </tr>
        </thead>

        <tbody>
          {TIME_AXIS.map((timeCode) => {
            const timeLabel = formatTimeCode(timeCode);

            return (
              <tr key={timeCode} className="border-t border-[#F3F4F6]">
                <td className="py-1.5 pl-2 text-[#9CA3AF] font-medium sticky left-0 bg-white z-10 text-[11px] whitespace-nowrap pr-2">
                  {timeLabel}
                </td>

                {weekDates.map((dateStr, index) =>
                  (index === 0 || index === 6) ? null : 
                  selectedShops.map((shop, shopIndex) => {
                    const firstCell = shopIndex == 0
                    const badgeClass = getShopBadgeClass(shopIndex);
                    const slots = openSlotsByDateAndShopId[dateStr]?.[shop.id] || [];
                    const slot = slots.find(
                      (item) => item.startTimeCode === timeCode
                    );

                    const isSelected =
                      slot &&
                      selectedOpenSlotIdsByShopId[shop.id] === slot.id;

                    const isDimmed =
                      selectedDate !== null && selectedDate !== dateStr;
                    
                    if (!slot || slot.slotStatus === "CLOSED") {
                      return (
                        <td
                          key={`${dateStr}-${shop.id}-${timeCode}`}
                          className={`py-1 px-1 border-l border-l-${firstCell ? DATE_BORDER_COLOR : '[#F3F4F6]'} ${
                            isDimmed ? "opacity-30" : ""
                          }
                          ${selectedDate === dateStr  ? "bg-[#EFF6FF]" : selectedDate == today ? "bg-[#F9FAFB]" : ""}
                          `}
                          style={firstCell ? { borderLeft: `1px solid ${DATE_BORDER_COLOR}`} : {}}
                        >
                          <div className="w-full h-7 rounded-md bg-[#F9FAFB] border border-[#F3F4F6] flex items-center justify-center">
                            <span className="text-[#D1D5DB] text-[10px]">—</span>
                          </div>
                        </td>
                      );
                    }

                    if (slot.slotStatus === "BOOKED") {
                      return (
                        <td
                          key={`${dateStr}-${shop.id}-${timeCode}`}
                          className={`py-1 px-1 border-l border-l-${firstCell ? DATE_BORDER_COLOR : '[#F3F4F6]'} ${
                            isDimmed ? "opacity-30" : ""
                          }`}
                          style={firstCell ? { borderLeft: `1px solid ${DATE_BORDER_COLOR}`} : {}}
                        >
                          <div className="w-full h-7 rounded-md bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center">
                            <span className="text-[#9CA3AF] text-[10px] line-through">
                              예약완료
                            </span>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={`${dateStr}-${shop.id}-${timeCode}`}
                        className={`py-1 px-1 border-l border-l-${firstCell ? DATE_BORDER_COLOR : '[#F3F4F6]'} ${
                          isDimmed ? "opacity-40" : ""
                        }
                        ${selectedDate === dateStr  ? "bg-[#EFF6FF]" : selectedDate == today ? "bg-[#F9FAFB]" : ""}
                        `}
                        style={firstCell ? { borderLeft: `1px solid ${DATE_BORDER_COLOR}`} : {}}
                      >
                        <button
                          onClick={() => onSelectOpenSlot(slot)}
                          className={`w-full h-7 rounded-md border text-[10px] font-semibold transition-all cursor-pointer ${
                            isSelected
                              ? "bg-[#2563EB] text-white border-[#2563EB] scale-105"
                              : `bg-white text-[#374151] border-[#bbb] hover:border-[#2563EB] hover:bg-[#EFF6FF]`
                          }`}
                        >
                          {isSelected ? "✓" : "가능"}
                        </button>
                      </td>
                    );
                  })
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SlotLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {[
        { cls: "bg-white border border-[#E5E7EB]", label: "예약가능" },
        { cls: "bg-[#2563EB]", label: "선택됨" },
        { cls: "bg-[#F3F4F6] border border-[#E5E7EB]", label: "예약됨" },
        { cls: "bg-[#F9FAFB] border border-[#F3F4F6]", label: "운영안함" },
      ].map(({ cls, label }) => (
        <div key={label} className="flex items-center gap-1.5">
          <div className={`w-4 h-4 rounded ${cls}`} />
          <span className="text-[11px] text-[#6B7280]">{label}</span>
        </div>
      ))}
    </div>
  );
}

type MobileStep2Props = {
  shops: ShopRow[];
  selectedShopIds: ShopId[];
  weekDates: DateString[];
  currentWeekStart: Date;
  openSlotsByDateAndShopId: OpenSlotsByDateAndShopId;
  selectedOpenSlotIdsByShopId: SelectedOpenSlotIdsByShopId;
  onSelectOpenSlot: (openSlot: OpenSlotRow) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onBack: () => void;
  onNext: () => void;
};

function MobileStep2({
  shops,
  selectedShopIds,
  weekDates,
  currentWeekStart,
  openSlotsByDateAndShopId,
  selectedOpenSlotIdsByShopId,
  onSelectOpenSlot,
  onPrevWeek,
  onNextWeek,
  onToday,
  onBack,
  onNext,
}: MobileStep2Props) {
  const selectedShops = useMemo(
    () =>
      selectedShopIds
        .map((shopId) => shops.find((shop) => shop.id === shopId))
        .filter((shop): shop is ShopRow => Boolean(shop)),
    [selectedShopIds, shops]
  );

  const selectedOpenSlots = useMemo(() => {
    return Object.entries(selectedOpenSlotIdsByShopId)
      .map(([shopId, openSlotId]) => {
        const shop = shops.find((item) => item.id === Number(shopId));
        if (!shop) return null;

        for (const dateStr of weekDates) {
          const slots =
            openSlotsByDateAndShopId[dateStr]?.[Number(shopId)] || [];
          const matched = slots.find((slot) => slot.id === openSlotId);
          if (matched) {
            return { shop, openSlot: matched };
          }
        }

        return null;
      })
      .filter(
        (
          item
        ): item is {
          shop: ShopRow;
          openSlot: WeeklyOpenSlotItem;
        } => Boolean(item)
      )
      .sort((a, b) => a.openSlot.startTimeCode - b.openSlot.startTimeCode);
  }, [openSlotsByDateAndShopId, selectedOpenSlotIdsByShopId, shops, weekDates]);
  
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2.5 bg-white border-b border-[#E5E7EB] flex-shrink-0">
        <div className="flex flex-wrap gap-1.5">
          {selectedShops.map((shop, idx) => {
            const badgeClass = getShopBadgeClass(idx);
            return (
              <span
                key={shop.id}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${badgeClass.bg} ${badgeClass.text} ${badgeClass.border}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${badgeClass.dot}`} />
                {shop.shopName}
              </span>
            );
          })}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-1.5">
          같은 날짜의 각 업체 슬롯을 선택하세요 · 다른 날짜 선택 시 초기화
        </p>
      </div>

      <div className="px-4 py-2 bg-[#FAFAFA] border-b border-[#E5E7EB] flex items-center justify-between flex-shrink-0 sticky top-0 z-10">
        <div className="flex items-center gap-1">
          <button
            onClick={onPrevWeek}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg text-[#374151]" />
          </button>
          <span className="text-xs font-semibold text-[#111827] whitespace-nowrap px-1">
            {formatDateRange(currentWeekStart)}
          </span>
          <button
            onClick={onNextWeek}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg text-[#374151]" />
          </button>
        </div>
        <button
          style={{display: 'none'}}
          onClick={onToday}
          className="px-3 py-1.5 text-xs font-semibold text-[#2563EB] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors cursor-pointer whitespace-nowrap"
        >
          오늘
        </button>
      </div>

      <div className="flex-1 overflow-auto px-3 py-3">
        <SlotGrid
          shops={shops}
          selectedShopIds={selectedShopIds}
          weekDates={weekDates}
          openSlotsByDateAndShopId={openSlotsByDateAndShopId}
          selectedOpenSlotIdsByShopId={selectedOpenSlotIdsByShopId}
          onSelectOpenSlot={onSelectOpenSlot}
        />
      </div>

      <div className="flex-shrink-0 px-4 py-2.5 border-t border-[#F3F4F6] bg-white">
        <SlotLegend />
      </div>

      <div className="flex-shrink-0 bg-white border-t border-[#E5E7EB] px-4 py-4">
        {selectedOpenSlots.length > 0 && (
          <div className="mb-3 px-3 py-2.5 bg-[#EFF6FF] rounded-xl border border-[#BFDBFE]">
            <p className="text-xs font-semibold text-[#1D4ED8] mb-1">
              {formatDateFull(selectedOpenSlots[0].openSlot.slotDate)} 선택됨
            </p>
            <div className="flex flex-wrap gap-1.5">
              {selectedOpenSlots.map(({ shop, openSlot }, idx) => {
                const badgeClass = getShopBadgeClass(
                  selectedShopIds.indexOf(shop.id)
                );
                return (
                  <span
                    key={idx}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${badgeClass.bg} ${badgeClass.text} ${badgeClass.border}`}
                  >
                    {formatTimeCode(openSlot.startTimeCode)}{" "}
                    {shop.shopName.length > 6
                      ? `${shop.shopName.slice(0, 6)}…`
                      : shop.shopName}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onBack}
            className="px-4 py-3.5 bg-[#F3F4F6] text-[#374151] text-sm font-semibold rounded-xl hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line text-sm" />
            이전
          </button>
          <button
            onClick={onNext}
            disabled={Object.keys(selectedOpenSlotIdsByShopId).length === 0}
            className={`flex-1 py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
              Object.keys(selectedOpenSlotIdsByShopId).length > 0
                ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                : "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
            }`}
          >
            <i className="ri-check-double-line text-base" />
            선택 일정 확인 ({Object.keys(selectedOpenSlotIdsByShopId).length})
          </button>
        </div>
      </div>
    </div>
  );
}


function parseSelectedShopIds(value: string | null): ShopId[] {
  if (!value) return [];

  return value
    .split(',')
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v)) as ShopId[];
}

function stringifySelectedShopIds(value: ShopId[]): string {
  return value.join(',');
}

function parseSelectedSlots(value: string | null): SelectedOpenSlotIdsByShopId {
  if (!value) return {};

  return value.split('|').reduce<SelectedOpenSlotIdsByShopId>((acc, pair) => {
    const [shopIdStr, slotIdStr] = pair.split(':');
    const shopId = Number(shopIdStr);
    const slotId = Number(slotIdStr);

    if (Number.isFinite(shopId) && Number.isFinite(slotId)) {
      acc[shopId] = slotId;
    }

    return acc;
  }, {});
}

function stringifySelectedSlots(value: SelectedOpenSlotIdsByShopId): string {
  return Object.entries(value)
    .map(([shopId, slotId]) => `${shopId}:${slotId}`)
    .join('|');
}

function parseWeekStart(value: string | null): Date {
  if (!value) return getWeekStart(new Date());

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return getWeekStart(new Date());
  }

  return getWeekStart(date);
}

export default function WeeklyMulityShopSelectorPage() {
  const navigate = useNavigate();
  const selectedDateRef = useRef<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { shops } = useAppSelector((state: RootState) => state.shop);

  const [openSlots, setOpenSlots] = useState<WeeklyOpenSlotItem[]>([]);
  const selectedShopIds = useMemo<ShopId[]>(() => {
    const raw = searchParams.get('selectedShopIds');
    if (!raw) return [];
    return raw
      .split(',')
      .map(Number)
      .filter((v) => Number.isFinite(v)) as ShopId[];
  }, [searchParams]);

  const selectedOpenSlotIdsByShopId = useMemo<SelectedOpenSlotIdsByShopId>(() => {
    const raw = searchParams.get('selectedSlots');
    if (!raw) return {};

    return raw.split('|').reduce<SelectedOpenSlotIdsByShopId>((acc, pair) => {
      const [shopIdStr, slotIdStr] = pair.split(':');
      const shopId = Number(shopIdStr);
      const slotId = Number(slotIdStr);

      if (Number.isFinite(shopId) && Number.isFinite(slotId)) {
        acc[shopId] = slotId;
      }

      return acc;
    }, {});
  }, [searchParams]);
  const updateSearchParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      updater(next);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const currentWeekStart = useMemo<Date>(() => {
    return parseWeekStart(searchParams.get('weekStart') || '2026-10-01');
  }, [searchParams]);

  const [mobileStep, setMobileStep] = useState<MobileStep>(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shopLoading, setShopLoading] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);

  const weekDates = useMemo(
    () => generateWeekDates(currentWeekStart),
    [currentWeekStart]
  );

  const startDate = weekDates[0];
  const endDate = weekDates[6];

  const selectedShops = useMemo(() => {
    if (shops.length === 0) {
      return []
    }

    const shopMap = new Map()
    shops.forEach(shop => {
      shopMap.set(shop.id, shop)
    })
    return selectedShopIds.map(id => shopMap.get(id))
  }, [shops, selectedShopIds])

  useEffect(() => {
    let mounted = true;

    async function loadOpenSlots() {
      try {
        // setSlotLoading(true);
        if (selectedShopIds.length > 0) {
          const { data: openSlots } = await fetchWeeklyOpenSlots({
            startDate,
            endDate,
            shopIds: selectedShopIds
          });
          if (!mounted) return;
          setOpenSlots(openSlots);
        }
      } finally {
        if (mounted) setSlotLoading(false);
      }
    }

    loadOpenSlots();

    return () => {
      mounted = false;
    };
  }, [endDate, selectedShopIds, startDate]);
  useEffect(() => {
    try {
      trackEvent(GA_EVENT.SLOT_VIEW, {
        booking_path: "DIRECT_SLOT",
      });
    } catch (e) {

    }
  }, []);
  // useEffect(() => {
  //   if (!selectedDateRef.current) {
  //     selectedDateRef.current = searchParams.get('selectedDate')
  //   }
  // }, [searchParams])

  const openSlotsByDateAndShopId = useMemo<OpenSlotsByDateAndShopId>(() => {
    const map: OpenSlotsByDateAndShopId = {};

    for (const dateStr of weekDates) {
      map[dateStr] = {} as Record<number, WeeklyOpenSlotItem[]>;
      for (const shopId of selectedShopIds) {
        map[dateStr][shopId] = [];
      }
    }
    
    openSlots.forEach((slot) => {
      if (!map[slot.slotDate]) {
        map[slot.slotDate] = {} as Record<number, WeeklyOpenSlotItem[]>;
      }
      if (!map[slot.slotDate][slot.shopId]) {
        map[slot.slotDate][slot.shopId] = [];
      }
      map[slot.slotDate][slot.shopId].push(slot);
    });

    Object.values(map).forEach((shopMap) => {
      Object.values(shopMap).forEach((slots) => {
        slots.sort((a, b) => a.startMinuteOfDay - b.startMinuteOfDay);
      });
    });

    return map;
  }, [openSlots, selectedShopIds, weekDates]);

  const handleToggleShop = useCallback((shopId: ShopId) => {
    updateSearchParams((params) => {
      const prevShopIds = parseSelectedShopIds(params.get('selectedShopIds'));
      const nextShopIds = prevShopIds.includes(shopId)
        ? prevShopIds.filter((id) => id !== shopId)
        : [...prevShopIds, shopId];

      if (nextShopIds.length > 0) {
        params.set('selectedShopIds', stringifySelectedShopIds(nextShopIds));
      } else {
        params.delete('selectedShopIds');
      }

      const prevSlots = parseSelectedSlots(params.get('selectedSlots'));
      delete prevSlots[shopId];

      const nextSlotsStr = stringifySelectedSlots(prevSlots);
      if (nextSlotsStr) {
        params.set('selectedSlots', nextSlotsStr);
      } else {
        params.delete('selectedSlots');
        params.delete('selectedDate');
      }
    });
  }, [updateSearchParams]);

  const handleSelectOpenSlot = useCallback((openSlot: OpenSlotRow) => {
    updateSearchParams((params) => {
      const prev = parseSelectedSlots(params.get('selectedSlots'));
      const sameShopSelectedId = prev[openSlot.shopId];

      if (sameShopSelectedId === openSlot.id) {
        const next = { ...prev };
        delete next[openSlot.shopId];

        const nextStr = stringifySelectedSlots(next);
        if (nextStr) {
          params.set('selectedSlots', nextStr);
        } else {
          params.delete('selectedSlots');
          params.delete('selectedDate');
        }

        if (Object.keys(next).length > 0) {
          const remainingSelectedSlotIds = Object.values(next);
          const remainingRows = openSlots.filter((slot) =>
            remainingSelectedSlotIds.includes(slot.id)
          );
          if (remainingRows[0]?.slotDate) {
            params.set('selectedDate', remainingRows[0].slotDate);
            selectedDateRef.current = remainingRows[0].slotDate;
          }
        } else {
          selectedDateRef.current = null;
        }

        return;
      }

      const prevEntries = Object.entries(prev);

      if (prevEntries.length === 0) {
        const next = { [openSlot.shopId]: openSlot.id };
        params.set('selectedSlots', stringifySelectedSlots(next));
        params.set('selectedDate', openSlot.slotDate);
        selectedDateRef.current = openSlot.slotDate;
        return;
      }

      const selectedSlotIds = Object.values(prev);
      const selectedRows = openSlots.filter((slot) => selectedSlotIds.includes(slot.id));
      const selectedDate = selectedRows[0]?.slotDate ?? null;

      if (selectedDate && selectedDate !== openSlot.slotDate) {
        const next = { [openSlot.shopId]: openSlot.id };
        params.set('selectedSlots', stringifySelectedSlots(next));
        params.set('selectedDate', openSlot.slotDate);
        selectedDateRef.current = openSlot.slotDate;
        return;
      }

      const next = { ...prev };

      const sameTimeSlot = selectedRows.find((selectedSlot) => {
        return selectedSlot.startMinuteOfDay === openSlot.startMinuteOfDay;
      });

      if (sameTimeSlot) {
        delete next[sameTimeSlot.shopId];
      }

      next[openSlot.shopId] = openSlot.id;

      params.set('selectedSlots', stringifySelectedSlots(next));
      params.set('selectedDate', openSlot.slotDate);
      selectedDateRef.current = openSlot.slotDate;
    });
  }, [openSlots, updateSearchParams]);

  const handleReset = useCallback(() => {
    selectedDateRef.current = null;
    setMobileStep(1);
    setDrawerOpen(false);

    updateSearchParams((params) => {
      params.delete('selectedShopIds');
      params.delete('selectedSlots');
      params.delete('selectedDate');
    });
  }, [updateSearchParams]);

  const handleConfirm = useCallback(() => {
    const openSlotIds = Object.values(selectedOpenSlotIdsByShopId);
    const selectedRows = openSlots
      .filter((slot) => openSlotIds.includes(slot.id))
      .sort((a, b) => a.startMinuteOfDay - b.startMinuteOfDay);
    const selectedDate = selectedDateRef.current || selectedRows?.[0].slotDate;
    try {
      trackEvent(
        GA_EVENT.SLOT_SELECT,
        buildDirectSlotSelectAnalyticsPayload({
          selectedRows,
          selectedShopIds,
        })
      );
    } catch (e) {

    }
    navigate({
      pathname: "/confirmation",
      search: buildSearch({
        slotIds: openSlotIds.join(','),
        selectedShopIds: selectedShopIds.join(','),
        selectedSlots: stringifySelectedSlots(selectedOpenSlotIdsByShopId),
        weekStart: dateToString(currentWeekStart),
        tourSize: String(selectedShopIds.length),
        selectedDate,
        bookingPath: DIRECT_SLOT
      })
    }, {
      state: {
        selectedShopIds,
        selectedDate: selectedRows[0]?.slotDate || null,
        openSlotIds,
      },
    });
  }, [
    currentWeekStart,
    navigate,
    openSlots,
    selectedOpenSlotIdsByShopId,
    selectedShopIds,
  ]);
  const handlePrevWeek = useCallback(() => {
    updateSearchParams((params) => {
      const current = parseWeekStart(params.get('weekStart'));
      const d = new Date(current);
      d.setDate(d.getDate() - 7);
      params.set('weekStart', dateToString(getWeekStart(d)));
    });
  }, [updateSearchParams]);

  const handleNextWeek = useCallback(() => {
    updateSearchParams((params) => {
      const current = parseWeekStart(params.get('weekStart'));
      const d = new Date(current);
      d.setDate(d.getDate() + 7);
      params.set('weekStart', dateToString(getWeekStart(d)));
    });
  }, [updateSearchParams]);

  const handleToday = useCallback(() => {
    updateSearchParams((params) => {
      params.set('weekStart', dateToString(getWeekStart(new Date())));
    });
  }, [updateSearchParams]);

  const dressTourTimeLineSlots = useMemo<DressTourTimelineSlot[]>(() => {
    const selectedOpenSlotIds = Object.values(selectedOpenSlotIdsByShopId);

    if (selectedOpenSlotIds.length === 0) {
      return [];
    }

    return openSlots
      .filter((slot) => selectedOpenSlotIds.includes(slot.id))
      .map((slot) => {
        const shop = shops.find((item) => item.id === slot.shopId);

        return {
          id: slot.id,
          shopId: slot.shopId,
          shopName: shop?.shopName || "",
          time: formatTimeCode(slot.startTimeCode),
          startMinuteOfDay: slot.startMinuteOfDay,
          slotDate: slot.slotDate,
          startTimeCode: slot.startTimeCode,
          penaltyLink: shop?.penaltyLink || null,
          phoneNumber: shop?.phoneNumber || null,
          naverPlaceId: (shop as any)?.naverPlaceId || null,
        };
      })
      .sort((a, b) => a.startMinuteOfDay - b.startMinuteOfDay);

  }, [openSlots, selectedOpenSlotIdsByShopId, shops]);

  useEffect(() => {
    if (openSlots.length === 0) return;

    const validSlotIds = new Set(openSlots.map((slot) => slot.id));
    const prev = selectedOpenSlotIdsByShopId;

    const next = Object.entries(prev).reduce<SelectedOpenSlotIdsByShopId>((acc, [shopId, slotId]) => {
      if (validSlotIds.has(slotId)) {
        acc[Number(shopId)] = slotId;
      }
      return acc;
    }, {});

    const prevStr = stringifySelectedSlots(prev);
    const nextStr = stringifySelectedSlots(next);

    if (prevStr !== nextStr) {
      updateSearchParams((params) => {
        if (nextStr) {
          params.set('selectedSlots', nextStr);
        } else {
          params.delete('selectedSlots');
          params.delete('selectedDate');
        }
      });
    }
  }, [openSlots, selectedOpenSlotIdsByShopId, updateSearchParams]);

  return (
    <>
      <div className="md:hidden flex flex-col h-screen bg-[#F5F6F8]">
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-3.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => {
              if (mobileStep === 1) navigate(-1);
              else setMobileStep((prev) => (prev - 1) as MobileStep);
            }}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-[#374151]" />
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-[#9CA3AF] font-medium tracking-wide uppercase">
            </p>
            <h2 className="text-base font-bold text-[#111827] leading-tight">
              {mobileStep === 1 && "업체 선택"}
              {mobileStep === 2 && "슬롯 선택"}
              {mobileStep === 3 && "일정 확인"}
            </h2>
          </div>

          {mobileStep > 1 && (
            <button
              onClick={handleReset}
              className="px-3 py-1.5 text-xs font-medium text-[#9CA3AF] bg-[#F3F4F6] rounded-lg hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap"
            >
              초기화
            </button>
          )}
        </div>

        <StepIndicator currentStep={mobileStep} />

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {mobileStep === 1 && (
            <ShopSelectPanel
              shops={shops}
              selectedShopIds={selectedShopIds}
              onToggleShop={handleToggleShop}
              onNext={() => setMobileStep(2)}
              mobile
            />
          )}

          {mobileStep === 2 && (
            <div className="flex flex-col h-full">
              {shopLoading || slotLoading ? (
                <div className="flex-1 flex items-center justify-center text-sm text-[#6B7280]">
                  데이터를 불러오는 중입니다...
                </div>
              ) : (
                <MobileStep2
                  shops={shops}
                  selectedShopIds={selectedShopIds}
                  weekDates={weekDates}
                  currentWeekStart={currentWeekStart}
                  openSlotsByDateAndShopId={openSlotsByDateAndShopId}
                  selectedOpenSlotIdsByShopId={selectedOpenSlotIdsByShopId}
                  onSelectOpenSlot={handleSelectOpenSlot}
                  onPrevWeek={handlePrevWeek}
                  onNextWeek={handleNextWeek}
                  onToday={handleToday}
                  onBack={() => setMobileStep(1)}
                  onNext={() => setMobileStep(3)}
                />
              )}
            </div>
          )}

          {mobileStep === 3 && (
            <div className="flex flex-col h-full bg-white">
              <div className='p-4'>
                <ReservationDateViewer targetDate={selectedDateRef.current} />
              </div>
              <div className="min-h-0 overflow-y-auto p-4 pt-0">
                <DressTourTimeLine slots={dressTourTimeLineSlots} />
              </div>
              <div style={{flex: 1}}>
              
              {
                selectedShops.length > 0 && (
                  <ShopNaverMap
                    slots={selectedShops}
                    style={{height: '100%'}}
                    minHeight={1}
                  />
                )
              }
              </div>
              <div className="flex-shrink-0 border-t border-[#E5E7EB] px-4 py-4 bg-white space-y-2">
                <button
                  onClick={handleConfirm}
                  className="w-full py-3.5 bg-[#2563EB] text-white text-sm font-bold rounded-xl hover:bg-[#1D4ED8] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-check-line text-base" />
                  예약 진행
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={() => setMobileStep(2)}
                    className="flex-1 py-3 bg-[#F3F4F6] text-[#374151] text-sm font-semibold rounded-xl hover:bg-[#E5E7EB] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-arrow-left-line text-sm" />
                    이전
                  </button>

                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 bg-[#FFF1F2] text-[#BE123C] text-sm font-semibold rounded-xl hover:bg-[#FFE4E6] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  >
                    <i className="ri-refresh-line text-sm" />
                    전체 초기화
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="hidden md:flex flex-col h-screen bg-[#F5F6F8]">
        <div className="bg-white border-b border-[#E5E7EB] px-5 py-4 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-[#374151]" />
          </button>
          <div>
            <p className="text-[11px] text-[#9CA3AF] font-medium tracking-wide uppercase">
              주간 일정 조립기
            </p>
            <h2 className="text-base font-bold text-[#111827] leading-tight">
              업체 선택 &amp; 슬롯 조립
            </h2>
          </div>
          <div className="ml-auto flex items-center gap-2" style={{display: 'none'}}>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg">
              <i className="ri-tools-line text-[#16A34A] text-sm" />
              <span className="text-xs text-[#15803D] font-medium">일정 조립기</span>
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-64 flex-shrink-0 bg-white border-r border-[#E5E7EB]">
            <ShopSelectPanel
              shops={shops}
              selectedShopIds={selectedShopIds}
              onToggleShop={handleToggleShop}
            />
          </div>

          <div className="flex-1 flex flex-col bg-white min-w-0">
            <div className="flex-shrink-0 px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={handlePrevWeek}
                    className="w-8 h-8 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg text-[#374151]" />
                  </button>
                  <span className="text-sm font-semibold text-[#111827] text-center whitespace-nowrap px-1" style={{minWidth: 100}}>
                    {formatDateRange(currentWeekStart)}
                  </span>
                  <button
                    onClick={handleNextWeek}
                    className="w-8 h-8 flex items-center justify-center hover:bg-[#F3F4F6] rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg text-[#374151]" />
                  </button>
                </div>

                <button
                  style={{display: 'none'}}
                  onClick={handleToday}
                  className="px-3 py-1.5 text-xs font-medium text-[#2563EB] bg-[#EFF6FF] rounded-lg hover:bg-[#DBEAFE] transition-colors cursor-pointer whitespace-nowrap"
                >
                  오늘
                </button>
              </div>

              <button
                onClick={() => setDrawerOpen(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  Object.keys(selectedOpenSlotIdsByShopId).length > 0
                    ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
                    : "bg-[#F3F4F6] text-[#9CA3AF]"
                }`}
              >
                <i className="ri-layout-right-2-line text-base" />
                선택 일정 보기 ({Object.keys(selectedOpenSlotIdsByShopId).length})
              </button>
            </div>

            {selectedShopIds.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 text-center px-8 py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-[#F3F4F6] rounded-2xl mb-4">
                  <i className="ri-store-2-line text-2xl text-[#9CA3AF]" />
                </div>
                <p className="text-sm font-semibold text-[#374151]">업체를 선택해주세요</p>
                <p className="text-xs text-[#9CA3AF] mt-1.5 leading-relaxed">
                  왼쪽 목록에서 방문할 업체를 선택하면
                  <br />
                  주간 슬롯 그리드가 표시됩니다
                </p>
              </div>
            ) : shopLoading || slotLoading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-[#6B7280]">
                데이터를 불러오는 중입니다...
              </div>
            ) : (
              <>
                <div className="flex-shrink-0 px-5 py-2.5 bg-[#FAFAFA] border-b border-[#F3F4F6] flex items-center gap-2">
                  <i className="ri-information-line text-[#9CA3AF] text-sm" />
                  <p className="text-xs text-[#6B7280]">
                    같은 날짜의 각 업체 슬롯을 선택하세요 · 다른 날짜 선택 시 기존 선택이 초기화됩니다
                  </p>
                </div>

                <div className="flex-1 overflow-auto px-4 py-4">
                  <SlotGrid
                    shops={shops}
                    selectedShopIds={selectedShopIds}
                    weekDates={weekDates}
                    openSlotsByDateAndShopId={openSlotsByDateAndShopId}
                    selectedOpenSlotIdsByShopId={selectedOpenSlotIdsByShopId}
                    onSelectOpenSlot={handleSelectOpenSlot}
                  />
                </div>

                <div className="flex-shrink-0 px-5 py-3 border-t border-[#F3F4F6]">
                  <SlotLegend />
                </div>
              </>
            )}
          </div>

          <DesktopDrawer
            isOpen={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            weekDates={weekDates}
            selectedDate={selectedDateRef.current}
            selectedShops={selectedShops}
            dressTourTimeLineSlots={dressTourTimeLineSlots}
            onReset={handleReset}
            onConfirm={handleConfirm}
          />
        </div>
      </div>
    </>
  );
}